import {
	getPossibleTransitions,
	type Permission,
	type Ticket,
	TicketStatus,
	type TicketTransition,
} from '@cheqroom/api';
import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';

export const createTicketPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission: _hasAnyPermission, useTickets, useExport, usePdf } = permissions;

	const hasAnyPermission = (...required: Permission[]) => useTickets && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('TICKETS_WRITER', 'TICKETS_WRITER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_WRITER'));

	const canOpen = (own?: boolean) =>
		hasAnyPermission('TICKETS_OPENER', 'TICKETS_OPENER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_OPENER'));

	const canStart = (own?: boolean) =>
		hasAnyPermission('TICKETS_STARTER', 'TICKETS_STARTER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_STARTER'));

	const canClose = (own?: boolean) =>
		hasAnyPermission('TICKETS_CLOSER', 'TICKETS_CLOSER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_CLOSER'));

	const canCancel = (own?: boolean) =>
		hasAnyPermission('TICKETS_CANCELER', 'TICKETS_CANCELER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_CANCELER'));

	const canArchive = (own?: boolean) =>
		hasAnyPermission('TICKETS_ARCHIVER', 'TICKETS_ARCHIVER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_ARCHIVER'));

	const canLabel = (own?: boolean) =>
		hasAnyPermission('TICKETS_LABELER', 'TICKETS_LABELER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_LABELER'));

	const canDelete = (own?: boolean) =>
		hasAnyPermission('TICKETS_DELETER', 'TICKETS_DELETER_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_DELETER'));

	const canAssignee = (own?: boolean) =>
		hasAnyPermission('TICKETS_SET_ASSIGNEE', 'TICKETS_SET_ASSIGNEE_RESTRICTED') ||
		(own && hasAnyPermission('TICKETS_OWN_SET_ASSIGNEE'));

	const canTransitionTicket = (transition: TicketTransition, ticket: Pick<Ticket, 'status' | 'archived'>) => {
		const possibleTransitions = getPossibleTransitions({
			status: ticket.status,
			isArchived: !!ticket.archived,
		});
		if (possibleTransitions.length === 0) return false;
		if (!possibleTransitions.includes(transition)) return false;

		return true;
	};

	return {
		canCreate: () => canWriter(true),
		canUpdate: canWriter,
		canRead: (own?: boolean) =>
			hasAnyPermission('TICKETS_READER', 'TICKETS_READER_RESTRICTED') ||
			(!!own && hasAnyPermission('TICKETS_OWN_READER')),
		canDelete: (ticket?: Pick<Ticket, 'status'>, own?: boolean) =>
			(!ticket || [TicketStatus.DRAFT, TicketStatus.IN_PROGRESS, TicketStatus.OPEN].includes(ticket.status)) &&
			canDelete(own),
		canDuplicate: canWriter,
		canExport: () =>
			!!useExport && hasAnyPermission('TICKETS_EXPORTER', 'TICKETS_EXPORTER_RESTRICTED', 'TICKETS_OWN_EXPORTER'),
		canAddAttachment: (ownTicket?: boolean) =>
			hasAnyPermission('TICKETS_ATTACHMENTS_OWN_WRITER') ||
			(!!ownTicket && hasAnyPermission('TICKETS_OWN_ATTACHMENTS_OWN_WRITER')),
		canRemoveAttachment: (ownAttachment?: boolean, ownTicket?: boolean) =>
			hasAnyPermission('TICKETS_ATTACHMENTS_DELETER') ||
			(!!ownAttachment && hasAnyPermission('TICKETS_ATTACHMENTS_OWN_DELETER')) ||
			(!!ownAttachment && !!ownTicket && hasAnyPermission('TICKETS_OWN_ATTACHMENTS_OWN_DELETER')),
		canAddComment: (ownTicket?: boolean) =>
			hasAnyPermission('TICKETS_COMMENTS_OWN_WRITER') ||
			(!!ownTicket && hasAnyPermission('TICKETS_OWN_COMMENTS_OWN_WRITER')),
		canUpdateComment: (ownComment?: boolean, ownTicket?: boolean) =>
			(!!ownComment && hasAnyPermission('TICKETS_COMMENTS_OWN_WRITER')) ||
			(!!ownComment && !!ownTicket && hasAnyPermission('TICKETS_OWN_COMMENTS_OWN_WRITER')),
		// python-api `removeComment` always passes `ensureIsOwnComment`: only your own comment, whatever the right.
		canRemoveComment: (ownComment?: boolean, ownTicket?: boolean) =>
			!!ownComment &&
			(hasAnyPermission('TICKETS_COMMENTS_DELETER', 'TICKETS_COMMENTS_OWN_DELETER') ||
				(!!ownTicket && hasAnyPermission('TICKETS_OWN_COMMENTS_OWN_DELETER'))),
		canGenerateDocument: (own?: boolean) =>
			!!usePdf &&
			(hasAnyPermission('TICKETS_DOCUMENT_GENERATOR', 'TICKETS_DOCUMENT_GENERATOR_RESTRICTED') ||
				(!!own && hasAnyPermission('TICKETS_OWN_DOCUMENT_GENERATOR'))),
		canSetLabel: (ticket?: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			(!ticket || (![TicketStatus.CANCELLED, TicketStatus.CLOSED].includes(ticket.status) && !ticket.archived)) &&
			canLabel(own),
		canClearLabel: (ticket?: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			(!ticket || (![TicketStatus.CANCELLED, TicketStatus.CLOSED].includes(ticket.status) && !ticket.archived)) &&
			canLabel(own),
		canSetPriority: canWriter,
		canSetReporter: (own?: boolean) =>
			hasAnyPermission('TICKETS_SET_REPORTER', 'TICKETS_SET_REPORTER_RESTRICTED') ||
			(!!own && hasAnyPermission('TICKETS_OWN_SET_REPORTER')),
		canSetAssignee: canAssignee,
		canClearAssignee: canAssignee,
		canOpen: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('open', ticket) && canOpen(own),
		canUndoOpen: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('undoOpen', ticket) && canOpen(own),
		canStart: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('start', ticket) && canStart(own),
		canUndoStart: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('undoStart', ticket) && canStart(own),
		canClose: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('close', ticket) && canClose(own),
		canUndoClose: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('undoClose', ticket) && canClose(own),
		canCancel: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('cancel', ticket) && canCancel(own),
		canUndoCancel: (ticket: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			canTransitionTicket('undoCancel', ticket) && canCancel(own),
		canEditFields: (ticket?: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			(!ticket || (![TicketStatus.CANCELLED, TicketStatus.CLOSED].includes(ticket.status) && !ticket.archived)) &&
			canWriter(own),
		canArchive: (ticket?: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			(!ticket || ([TicketStatus.CLOSED].includes(ticket.status) && !ticket.archived)) && canArchive(own),
		canUndoArchive: (ticket?: Pick<Ticket, 'status' | 'archived'>, own?: boolean) =>
			!!ticket?.archived && canArchive(own),
		canUpgradePlan: () => permissions.canUpgrade(),
	};
};

export const useTicketPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createTicketPermissions(permissions), [permissions]);
};
